import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Typography, TextField, Switch, Autocomplete, Checkbox } from '@mui/material'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import LinearScaleIcon from '@mui/icons-material/LinearScale'
import TextFieldsIcon from '@mui/icons-material/TextFields'
import NotesIcon from '@mui/icons-material/Notes'
import ChecklistIcon from '@mui/icons-material/Checklist'
import StarOutlineIcon from '@mui/icons-material/StarOutline'
import { observer } from 'mobx-react-lite'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { appRoutePath } from 'src/feature/router/util/app-route-path'
import { ColorTokens, RadiusTokens } from 'src/feature/theme/model/Palette'
import { useMyTeamStore } from '../store/my-team.store'
import { AssignmentType, QuestionType, QuestionMetadata, UserInfo } from '../model/my-team-performance'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'

type HierarchicalOption = {
  id: string
  name: string
  type: AssignmentType
  parentId?: string
  dept: number
}

type DraftQuestion = {
  tempId: string
  questionText: string
  questionType: QuestionType
  isRequired: boolean
  metadata: QuestionMetadata
}

const typeLabels: Record<QuestionType, string> = {
  [QuestionType.TEXT_INPUT]: 'TEXT RESPONSE',
  [QuestionType.TEXTAREA]: 'TEXT AREA',
  [QuestionType.SLIDER]: 'SCALE',
  [QuestionType.RATING]: 'RATING SCALE',
  [QuestionType.MULTIPLE_CHOICE]: 'MULTIPLE CHOICE',
}

const addButtons: { type: QuestionType; label: string; icon: JSX.Element }[] = [
  { type: QuestionType.SLIDER, label: 'Scale', icon: <LinearScaleIcon sx={{ fontSize: 16 }} /> },
  { type: QuestionType.TEXT_INPUT, label: 'Text', icon: <TextFieldsIcon sx={{ fontSize: 16 }} /> },
  { type: QuestionType.TEXTAREA, label: 'Text area', icon: <NotesIcon sx={{ fontSize: 16 }} /> },
  { type: QuestionType.MULTIPLE_CHOICE, label: 'Choice', icon: <ChecklistIcon sx={{ fontSize: 16 }} /> },
  { type: QuestionType.RATING, label: 'Rating', icon: <StarOutlineIcon sx={{ fontSize: 16 }} /> },
]

const ratingLabels = ['POOR', 'FAIR', 'GOOD', 'GREAT', 'EXPERT']

const getDefaultMetadata = (type: QuestionType): QuestionMetadata => {
  if (type === QuestionType.SLIDER) {
    return { min: 0, max: 10 }
  }

  if (type === QuestionType.RATING) {
    return { min: 1, max: 5 }
  }

  if (type === QuestionType.MULTIPLE_CHOICE) {
    return { options: [{ id: `opt-${Date.now()}`, value: '' }] }
  }

  return {}
}

export const MyTeamPerformanceCreatePage = observer(() => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const myTeamStore = useMyTeamStore()
  const fromId = searchParams.get('from')
  const sourceTemplate = fromId
    ? myTeamStore.performanceReviewTemplates.find(t => t.id === fromId)
    : undefined

  const [name, setName] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [questions, setQuestions] = useState<DraftQuestion[]>([])
  const [selectedAssignments, setSelectedAssignments] = useState<HierarchicalOption[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { translation } = useLocalizationStore()
  const performanceReviewTranslation = translation.pages.myTeam.performanceReview

  useEffect(() => {
    myTeamStore.getOrganisationOverview()
  }, [myTeamStore])

  const getHierarchicalOptions = (): HierarchicalOption[] => {
    const orgs = myTeamStore.organisationOverview || []
    const options: HierarchicalOption[] = []
    options.push({ id: 'SELECT_ALL', name: 'Select All', type: AssignmentType.TEAM, dept: 0 })
    const teams = orgs.filter(org => org.levelType === 'team')
    teams.forEach((team) => {
      options.push({ id: team.id, name: team.name, type: AssignmentType.TEAM, dept: 1 })
      const roles = orgs.filter(org => org.levelType === 'role' && org.parentLevel === team.id)
      roles.forEach((role) => {
        options.push({ id: role.id, name: role.name, type: AssignmentType.ROLE, parentId: team.id, dept: 2 })
        role.users?.forEach((user: UserInfo) => {
          options.push({ id: user.id, name: `${user.firstName} ${user.lastName}`, type: AssignmentType.INDIVIDUAL, parentId: role.id, dept: 3 })
        })
      })
      team.users?.forEach((user: UserInfo) => {
        const inRole = roles.some(role => role.users?.some((u: UserInfo) => u.id === user.id))

        if (!inRole) {
          options.push({ id: user.id, name: `${user.firstName} ${user.lastName}`, type: AssignmentType.INDIVIDUAL, parentId: team.id, dept: 2 })
        }
      })
    })

    return options
  }

  const hierarchicalOptions = getHierarchicalOptions()

  const handleAssignmentChange = (selected: HierarchicalOption[]) => {
    const picked = selected.length > 0 ? selected[selected.length - 1] : null

    if (picked?.id === 'SELECT_ALL') {
      const allTeams = hierarchicalOptions.filter(o => o.type === AssignmentType.TEAM && o.id !== 'SELECT_ALL')
      setSelectedAssignments([picked, ...allTeams])

      return
    }

    const filtered = selected.filter(o => o.id !== 'SELECT_ALL')
    const ids = new Set(filtered.map(o => o.id))
    const optimized = filtered.filter(o => !o.parentId || !ids.has(o.parentId))
    setSelectedAssignments(optimized)
  }

  const isOptionDisabled = (option: HierarchicalOption) => {
    const hasSelectAll = selectedAssignments.some(o => o.id === 'SELECT_ALL')

    if (hasSelectAll && option.id !== 'SELECT_ALL') {
      return true
    }

    if (!option.parentId) {
      return false
    }

    if (selectedAssignments.some(o => o.id === option.parentId)) {
      return true
    }

    if (option.type === AssignmentType.INDIVIDUAL) {
      const parentRole = hierarchicalOptions.find(o => o.id === option.parentId)

      if (parentRole?.parentId) {
        return selectedAssignments.some(o => o.id === parentRole.parentId)
      }
    }

    return false
  }

  useEffect(() => {
    if (sourceTemplate?.questions) {
      const copied: DraftQuestion[] = sourceTemplate.questions.map((q, i) => ({
        tempId: `temp-${Date.now()}-${i}`,
        questionText: q.questionText,
        questionType: q.questionType,
        isRequired: q.isRequired,
        metadata: q.metadata?.options
          ? { ...q.metadata, options: (q.metadata.options as string[]).map((opt, idx) => ({ id: `opt-${i}-${idx}`, value: opt })) }
          : q.metadata ?? {},
      }))
      setQuestions(copied)
    }
  }, [sourceTemplate])

  const addQuestion = (type: QuestionType) => {
    setQuestions(prev => [...prev, {
      tempId: `temp-${Date.now()}`,
      questionText: '',
      questionType: type,
      isRequired: true,
      metadata: getDefaultMetadata(type),
    }])
  }

  const removeQuestion = (tempId: string) => {
    setQuestions(prev => prev.filter(q => q.tempId !== tempId))
  }

  const updateQuestionText = (tempId: string, value: string) => {
    setQuestions(prev => prev.map(q => q.tempId === tempId ? { ...q, questionText: value } : q))
  }

  const toggleRequired = (tempId: string) => {
    setQuestions(prev => prev.map(q => q.tempId === tempId ? { ...q, isRequired: !q.isRequired } : q))
  }

  const updateMetadata = (tempId: string, key: 'min' | 'max', value: number) => {
    setQuestions(prev => prev.map(q => q.tempId === tempId ? { ...q, metadata: { ...q.metadata, [key]: value } } : q))
  }

  const addOption = (tempId: string) => {
    setQuestions(prev => prev.map((q) => {
      if (q.tempId !== tempId) {
        return q
      }

      const opts = (q.metadata.options as Array<{ id: string; value: string }>) ?? []

      return { ...q, metadata: { ...q.metadata, options: [...opts, { id: `opt-${Date.now()}`, value: '' }] } }
    }))
  }

  const removeOption = (tempId: string, optionId: string) => {
    setQuestions(prev => prev.map((q) => {
      if (q.tempId !== tempId) {
        return q
      }

      const opts = (q.metadata.options as Array<{ id: string; value: string }>) ?? []

      return { ...q, metadata: { ...q.metadata, options: opts.filter(o => o.id !== optionId) } }
    }))
  }

  const updateOption = (tempId: string, optionId: string, value: string) => {
    setQuestions(prev => prev.map((q) => {
      if (q.tempId !== tempId) {
        return q
      }

      const opts = (q.metadata.options as Array<{ id: string; value: string }>) ?? []

      return { ...q, metadata: { ...q.metadata, options: opts.map(o => o.id === optionId ? { ...o, value } : o) } }
    }))
  }

  const handleSubmit = async () => {
    if (!name.trim() || questions.length === 0 || isSubmitting) {
      return
    }

    const picked = selectedAssignments.filter(o => o.id !== 'SELECT_ALL')
    const hasSelectAll = selectedAssignments.some(o => o.id === 'SELECT_ALL')
    const assignments = hasSelectAll
      ? picked.filter(o => o.type === AssignmentType.TEAM).map(o => ({ type: o.type, targetId: o.id }))
      : picked.filter((o) => {
        const parentSelected = o.parentId && picked.some(s => s.id === o.parentId)
        const isChild = o.type === AssignmentType.ROLE || o.type === AssignmentType.INDIVIDUAL

        return !(isChild && parentSelected)
      }).map(o => ({ type: o.type, targetId: o.id }))

    const questionsDto = questions.map((q, i) => ({
      questionText: q.questionText,
      questionType: q.questionType,
      order: i,
      isRequired: q.isRequired,
      metadata: q.metadata.options
        ? { ...q.metadata, options: (q.metadata.options as Array<{ id: string; value: string }>).map(o => o.value) }
        : q.metadata,
    }))

    setIsSubmitting(true)
    try {
      await myTeamStore.createPerformanceReviewTemplate({
        name,
        dueDate: dueDate || undefined,
        assignments,
        questions: questionsDto,
      })
      
      await myTeamStore.getAllPerformanceReviewTemplates()
      navigate(appRoutePath.myTeamPerformance)
    } finally {
      setIsSubmitting(false)
    }
  }

  const renderBody = (q: DraftQuestion) => {
    if (q.questionType === QuestionType.RATING) {
      return (
        <AppBox sx={styles.ratingRow}>
          {ratingLabels.map((label, i) => (
            <AppBox key={label} sx={styles.ratingItem}>
              <Typography sx={styles.ratingNum}>{i + 1}</Typography>
              <Typography sx={styles.ratingLabel}>{label}</Typography>
            </AppBox>
          ))}
        </AppBox>
      )
    }

    if (q.questionType === QuestionType.SLIDER) {
      return (
        <AppBox sx={styles.sliderRow}>
          <AppBox sx={styles.sliderField}>
            <Typography sx={styles.sliderFieldLabel}>{performanceReviewTranslation.min()}</Typography>
            <TextField
              type="number"
              value={q.metadata.min ?? 0}
              onChange={e => updateMetadata(q.tempId, 'min', Number(e.target.value))}
              size="small"
              sx={styles.input}
            />
          </AppBox>
          <AppBox sx={styles.sliderField}>
            <Typography sx={styles.sliderFieldLabel}>{performanceReviewTranslation.max()}</Typography>
            <TextField
              type="number"
              value={q.metadata.max ?? 10}
              onChange={e => updateMetadata(q.tempId, 'max', Number(e.target.value))}
              size="small"
              sx={styles.input}
            />
          </AppBox>
        </AppBox>
      )
    }

    if (q.questionType === QuestionType.MULTIPLE_CHOICE) {
      const opts = (q.metadata.options as Array<{ id: string; value: string }>) ?? []

      return (
        <AppBox sx={styles.optionList}>
          {opts.map(opt => (
            <AppBox key={opt.id} sx={styles.optionRow}>
              <RadioButtonUncheckedIcon sx={styles.optionRadio} />
              <TextField
                value={opt.value}
                onChange={e => updateOption(q.tempId, opt.id, e.target.value)}
                placeholder="Option text"
                fullWidth
                size="small"
                variant="standard"
                InputProps={{ disableUnderline: true, sx: { fontSize: 13, color: ColorTokens.secondary500 } }}
              />
              <DeleteOutlineIcon sx={styles.optionTrash} onClick={() => removeOption(q.tempId, opt.id)} />
            </AppBox>
          ))}
          <AppBox sx={styles.addOption} onClick={() => addOption(q.tempId)}>
            <AddCircleOutlineIcon sx={{ fontSize: 16 }} />
            <Typography sx={styles.addOptionText}>{performanceReviewTranslation.addOption()}</Typography>
          </AppBox>
        </AppBox>
      )
    }

    return null
  }

  return (
    <AppBox sx={styles.root}>
      <AppBox sx={styles.breadcrumb}>
        <Typography
          sx={styles.crumbLink}
          onClick={() => navigate(appRoutePath.myTeamPerformance)}
        >
          {performanceReviewTranslation.title()}
        </Typography>
        <Typography sx={styles.crumbSep}>{'>'}</Typography>
        <Typography sx={styles.crumbCurrent}>{performanceReviewTranslation.addTemplate()}</Typography>
      </AppBox>

      <Typography sx={styles.title}>{performanceReviewTranslation.addTemplate()}</Typography>

      <AppBox sx={styles.topRow}>
        <AppPaper sx={styles.nameCard}>
          <Typography sx={styles.fieldLabel}>{performanceReviewTranslation.templateName()}</Typography>
          <TextField
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. Quarterly Performance"
            fullWidth
            size="small"
            sx={styles.input}
          />
        </AppPaper>

        <AppPaper sx={styles.dateCard}>
          <Typography sx={styles.fieldLabel}>{performanceReviewTranslation.dueDate()}</Typography>
          <TextField
            type="date"
            value={dueDate}
            onChange={e => setDueDate(e.target.value)}
            fullWidth
            size="small"
            sx={styles.input}
          />
        </AppPaper>
      </AppBox>

      <Typography sx={styles.contentTitle}>{performanceReviewTranslation.templateContent()}</Typography>

      <AppBox sx={styles.questionList}>
        {questions.map((q, idx) => (
          <AppPaper key={q.tempId} sx={styles.questionCard}>
            <DeleteOutlineIcon sx={styles.trashIcon} onClick={() => removeQuestion(q.tempId)} />
            <AppBox sx={styles.questionTopRow}>
              <AppBox sx={styles.numberBadge}>{idx + 1}</AppBox>
              <Typography sx={styles.questionTypeLabel}>{typeLabels[q.questionType]}</Typography>
            </AppBox>
            <TextField
              value={q.questionText}
              onChange={e => updateQuestionText(q.tempId, e.target.value)}
              placeholder="Question text..."
              fullWidth
              size="small"
              sx={styles.input}
            />
            {renderBody(q)}
            <AppBox sx={styles.requiredRow}>
              <Typography sx={styles.requiredLabel}>{performanceReviewTranslation.required()}</Typography>
              <Switch
                size="small"
                checked={q.isRequired}
                onChange={() => toggleRequired(q.tempId)}
              />
            </AppBox>
          </AppPaper>
        ))}
      </AppBox>

      <AppBox sx={styles.addCard}>
        <AppBox sx={styles.addLabelBox}>
          <Typography sx={styles.addTitle}>{performanceReviewTranslation.addContent()}</Typography>
          <Typography sx={styles.addSubtitle}>{performanceReviewTranslation.questionType()}</Typography>
        </AppBox>
        <AppBox sx={styles.addBtnGroup}>
          {addButtons.map(btn => (
            <AppBox key={btn.type} sx={styles.addBtn} onClick={() => addQuestion(btn.type)}>
              {btn.icon}
              <Typography sx={styles.addBtnLabel}>{btn.label}</Typography>
            </AppBox>
          ))}
        </AppBox>
      </AppBox>

      <AppPaper sx={styles.assignCard}>
        <Typography sx={styles.fieldLabel}>{performanceReviewTranslation.assignTo()}</Typography>
        <Autocomplete
          multiple
          size="small"
          options={hierarchicalOptions}
          value={selectedAssignments}
          onChange={(_, v) => handleAssignmentChange(v)}
          getOptionLabel={o => o.name}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          getOptionDisabled={isOptionDisabled}
          renderOption={(props, option, { selected }) => {
            const getTypeBadge = () => {
              if (option.id === 'SELECT_ALL') {
                return null
              }

              if (option.type === AssignmentType.TEAM) {
                return { label: performanceReviewTranslation.assignmentTypes.team(), color: '#1976d2' }
              }

              if (option.type === AssignmentType.ROLE) {
                return { label: performanceReviewTranslation.assignmentTypes.role(), color: '#2e7d32' }
              }

              return { label: performanceReviewTranslation.assignmentTypes.user(), color: '#ed6c02' }
            }
            const typeBadge = getTypeBadge()

            return (
              <li {...props} style={{ paddingLeft: 8 + option.dept * 20, fontSize: 12 }}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  checked={selected}
                  size="small"
                  sx={{ padding: '4px', marginRight: '4px' }}
                />
                {typeBadge && (
                  <span style={{ color: typeBadge.color, fontSize: 12, fontWeight: 600, marginRight: 6 }}>
                    {typeBadge.label}
                  </span>
                )}
                <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                  {option.id === 'SELECT_ALL'
                    ? performanceReviewTranslation.selectAll()
                    : option.name}
                </span>
              </li>
            )
          }}
          slotProps={{
            paper: {
              sx: {
                minWidth: 480,
                '& .MuiAutocomplete-listbox': {
                  fontSize: 12,
                },
              },
            },
          }}
          renderInput={params => (
            <TextField {...params} placeholder={performanceReviewTranslation.createPlaceholder()} />
          )}
          sx={styles.input}
        />
      </AppPaper>

      <AppBox sx={styles.footerRow}>
        <AppBox
          sx={styles.cancelBtn}
          onClick={() => navigate(appRoutePath.myTeamPerformance)}
        >
          {performanceReviewTranslation.cancel()}
        </AppBox>
        <AppBox
          sx={(!name.trim() || questions.length === 0 || selectedAssignments.length === 0 || isSubmitting) ? styles.submitBtnDisabled : styles.submitBtn}
          onClick={handleSubmit}
        >
          {isSubmitting
            ? performanceReviewTranslation.creating()
            : performanceReviewTranslation.addTemplate()}
        </AppBox>
      </AppBox>
    </AppBox>
  )
})

const styles = {
  root: {
    flexDirection: 'column',
    gap: 12,
    padding: { xs: '1rem', md: '1rem 5rem' },
    width: '100%',
    maxWidth: 1300,
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  crumbLink: {
    fontSize: 13,
    color: 'text.secondary',
    cursor: 'pointer',
  },
  crumbSep: {
    fontSize: 13,
    color: 'text.secondary',
  },
  crumbCurrent: {
    fontSize: 13,
    color: ColorTokens.secondary500,
  },
  title: {
    fontSize: 36,
    fontWeight: 400,
    color: ColorTokens.secondary500,
  },
  topRow: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  nameCard: {
    flex: 2,
    padding: '20px 24px',
    borderRadius: '14px',
    flexDirection: 'column',
    gap: 6,
  },
  dateCard: {
    flex: 1,
    padding: '20px 24px',
    borderRadius: '14px',
    flexDirection: 'column',
    gap: 6,
  },
  fieldLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '1px',
    color: ColorTokens.neutral800,
  },
  input: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      fontSize: 14,
      backgroundColor: `${ColorTokens.neutral200}80`,
      color: ColorTokens.secondary500,
      '& fieldset': {
        border: 'none',
      },
    },
    '& .MuiOutlinedInput-input': {
      '&::placeholder': {
        color: ColorTokens.neutral800,
        opacity: 1,
      },
    },
  },
  contentTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: ColorTokens.secondary500,
    marginTop: 12,
  },
  questionList: {
    flexDirection: 'column',
    gap: 12,
  },
  questionCard: {
    padding: '16px 20px',
    borderRadius: '14px',
    borderLeft: `8px solid ${ColorTokens.primary500}`,
    flexDirection: 'column',
    gap: 12,
    position: 'relative',
  },
  questionTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  requiredRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 6,
  },
  requiredLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: ColorTokens.neutral800,
  },
  numberBadge: {
    width: 28,
    height: 28,
    borderRadius: '6px',
    backgroundColor: ColorTokens.primary100,
    color: ColorTokens.secondary500,
    fontSize: 13,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  questionTypeLabel: {
    flex: 1,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '1px',
    color: ColorTokens.neutral800,
  },
  trashIcon: {
    position: 'absolute',
    top: 12,
    right: 16,
    fontSize: 22,
    color: ColorTokens.red200,
    cursor: 'pointer',
  },
  addCard: {
    padding: '16px 20px',
    borderRadius: '14px',
    border: `1px dashed ${ColorTokens.border100}`,
    backgroundColor: 'transparent',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  addLabelBox: {
    flexDirection: 'column',
    gap: 2,
  },
  addTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: ColorTokens.secondary500,
  },
  addSubtitle: {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '1px',
    color: ColorTokens.neutral800,
  },
  addBtnGroup: {
    flexDirection: 'row',
    gap: 6,
    flexWrap: 'wrap',
  },
  addBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    padding: '6px 12px',
    borderRadius: '8px',
    backgroundColor: ColorTokens.neutral100,
    cursor: 'pointer',
    color: ColorTokens.secondary500,
  },
  addBtnLabel: {
    fontSize: 12,
    fontWeight: 600,
  },
  assignCard: {
    padding: '20px 24px',
    borderRadius: '14px',
    flexDirection: 'column',
    gap: 8,
    marginTop: 8,
  },
  footerRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 16,
  },
  cancelBtn: {
    padding: '10px 22px',
    borderRadius: RadiusTokens.pill,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    backgroundColor: ColorTokens.neutral200,
    color: ColorTokens.secondary500,
  },
  submitBtn: {
    padding: '10px 22px',
    borderRadius: RadiusTokens.pill,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    backgroundColor: ColorTokens.secondary500,
    color: ColorTokens.neutral100,
  },
  submitBtnDisabled: {
    padding: '10px 22px',
    borderRadius: RadiusTokens.pill,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'not-allowed',
    backgroundColor: ColorTokens.border100,
    color: ColorTokens.neutral800,
    opacity: 0.6,
  },
  ratingRow: {
    flexDirection: 'row',
    gap: 8,
  },
  ratingItem: {
    flex: 1,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    padding: '16px 8px',
    borderRadius: '10px',
    backgroundColor: `${ColorTokens.neutral200}80`,
    color: ColorTokens.secondary500,
  },
  ratingNum: {
    fontSize: 18,
    fontWeight: 700,
  },
  ratingLabel: {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '1px',
  },
  sliderRow: {
    flexDirection: 'row',
    gap: 12,
  },
  sliderField: {
    flex: 1,
    flexDirection: 'column',
    gap: 4,
  },
  sliderFieldLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '1px',
    color: ColorTokens.neutral800,
  },
  optionList: {
    flexDirection: 'column',
    gap: 6,
  },
  optionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    padding: '8px 12px',
    borderRadius: '10px',
    backgroundColor: `${ColorTokens.neutral200}80`,
  },
  optionRadio: {
    fontSize: 18,
    color: ColorTokens.neutral800,
    flexShrink: 0,
  },
  optionTrash: {
    fontSize: 18,
    color: ColorTokens.red200,
    cursor: 'pointer',
    flexShrink: 0,
  },
  addOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    color: ColorTokens.primary500,
    cursor: 'pointer',
    width: 'fit-content',
  },
  addOptionText: {
    fontSize: 12,
    fontWeight: 600,
  },
}
